import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Division } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { DivisionForm } from "../DivisionForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListDivisions.scss";

const divisionController = new Division();

export function ListDivisions({ divisionsActive, reload, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [divisions,       setDivisions]       = useState(null);
  const [divisionsFilter, setDivisionsFilter] = useState([]);
  const [search,          setSearch]          = useState("");
  const [showModal,       setShowModal]       = useState(false);
  const [selectedDiv,     setSelectedDiv]     = useState(null);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [confirmMsg,      setConfirmMsg]      = useState("");
  const [isDelete,        setIsDelete]        = useState(false);
  const [rowMenu,         setRowMenu]         = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".divisions-list__row-menu, .divisions-list__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (id, e) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      id,
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setDivisions(null);
        const res = await divisionController.getDivisions(accessToken, divisionsActive);
        setDivisions(res);
        setDivisionsFilter(res);
      } catch {
        setDivisions([]);
        setDivisionsFilter([]);
      }
    })();
  }, [divisionsActive, reload]);

  useEffect(() => {
    if (!divisions) return;
    if (!search) { setDivisionsFilter(divisions); return; }
    const q = search.toLowerCase();
    setDivisionsFilter(
      divisions.filter((d) =>
        d.code?.toLowerCase().includes(q) || d.name?.toLowerCase().includes(q)
      )
    );
  }, [search, divisions]);

  const openCreate = () => { setSelectedDiv(null); setShowModal(true); };
  const openEdit   = (d)  => { setSelectedDiv(d);   setShowModal(true); };

  const openToggle = (d) => {
    setSelectedDiv(d);
    setIsDelete(false);
    setConfirmMsg(`¿${d.active ? t("deactivate") : t("activate")} "${d.name}"?`);
    setShowConfirm(true);
  };

  const openDelete = (d) => {
    setSelectedDiv(d);
    setIsDelete(true);
    setConfirmMsg(`¿${t("delete")} "${d.name}"?`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await divisionController.updateDivision(accessToken, selectedDiv._id, { active: !selectedDiv.active });
      toast.success(selectedDiv.active ? t("division_deactivated") : t("division_activated"));
      setShowConfirm(false);
      onReload();
    } catch { toast.error(t("error_updating")); }
  };

  const handleDelete = async () => {
    try {
      await divisionController.deleteDivision(accessToken, selectedDiv._id);
      toast.success(t("division_deleted"));
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || t("error_deleting")); }
  };

  if (divisions === null) return <LoadingSpinner />;

  return (
    <div className="divisions-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="divisions-list__card">
        <div className="divisions-list__filters">
          <div className="search-user-wrapper">
            <img src={searchSvg} alt="search" className="search-user-icon" />
            <input
              className="search-user-input"
              placeholder={`${t("search")}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button icon className="add-division-btn" title={t("create")} onClick={openCreate}>
            <Icon name="plus" />
          </Button>
        </div>

        {divisionsFilter.length === 0 ? (
          <div className="list-empty-state">
                <Icon name="folder open outline" className="list-empty-state__icon" />
                <p className="list-empty-state__text">{t("no_results")}</p>
              </div>
        ) : (
          <div className="divisions-list__tbl-scroll">
            <Table className="divisions-table unstackable rtable-cards row-hover">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("code")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("name")}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {divisionsFilter.map((d) => (
                  <Table.Row key={d._id}>
                    <Table.Cell data-label={t("code")}>{d.code ? d.code.toUpperCase() : ""}</Table.Cell>
                    <Table.Cell data-label={t("name")}>{d.name}</Table.Cell>
                    <Table.Cell textAlign="center" data-label="" className="divisions-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="divisions-list__row-menu">
                        <button type="button" className="divisions-list__row-menu-btn" onClick={(e) => toggleRowMenu(d._id, e)} aria-label={t("actions")}>
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {rowMenu?.id === d._id && ReactDOM.createPortal(
                        <div
                          className="divisions-list__row-menu-list"
                          style={{
                            position: "fixed",
                            top: rowMenu.top,
                            left: rowMenu.left,
                            transform: rowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { openEdit(d); setRowMenu(null); }}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openToggle(d); setRowMenu(null); }}>
                            <Icon name={d.active ? "ban" : "check"} />{d.active ? t("deactivate") : t("activate")}
                          </button>
                          <button className="divisions-list__row-menu-item--danger" onClick={() => { openDelete(d); setRowMenu(null); }}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selectedDiv ? `${t("edit")}: ${selectedDiv.name}` : t("new_division")}
        size="small"
        className="division-modal"
      >
        <DivisionForm
          division={selectedDiv}
          close={() => setShowModal(false)}
          onReload={onReload}
        />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? "Eliminar" : (selectedDiv?.active ? "Desactivar" : "Activar")}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel="Aceptar"
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}
